import {
  type BaseQueryFn,
  type FetchArgs,
  fetchBaseQuery,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { baseUrl, RequestStatuses } from '~/redux/api/constants';
import { AuthAccessor, LocalStorageApi } from '~/utils/storage';
import { loginApi } from '~/redux/api/routes/loginApi';
import type { APIErrorResponse } from '~/redux/api/models';
import { authExcludedEndpoints } from '~/redux/api/constants';
import { errorSerializer } from '~/utils';
import { RoutePaths } from '~/routes/constants';
import { USER_EMAIL, USER_ID } from '~/utils/storage/contants';
import i18n from '~/utils/i18n';

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { endpoint }) => {
    const accessToken = AuthAccessor.getAccessToken();

    headers.set('x-language', i18n.resolvedLanguage || i18n.language);
    // Allow to use new API version.
    headers.set('x-api-version', '2');

    headers.set('x-platform', 'web');

    if (!authExcludedEndpoints.includes(endpoint) && accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return headers;
  },
});

export const baseQueryWithAuth: BaseQueryFn<
  FetchArgs | string,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === RequestStatuses.UNAUTHORIZED) {
    const refreshToken = AuthAccessor.getRefreshToken();
    if (!refreshToken) {
      LocalStorageApi?.removeItem(USER_ID);
      LocalStorageApi?.removeItem(USER_EMAIL);
      AuthAccessor.clearTokens();

      if (!location.pathname.includes(RoutePaths.LOGIN)) {
        window.location.replace(RoutePaths.LOGIN);
      }

      return result;
    }

    try {
      const refreshResult = await api.dispatch(
        loginApi.endpoints.refreshAccessToken.initiate({
          refresh_token: refreshToken,
        })
      );

      if (!refreshResult.data) {
        LocalStorageApi?.removeItem(USER_ID);
        LocalStorageApi?.removeItem(USER_EMAIL);
        AuthAccessor.clearTokens();

        if (!location.pathname.includes(RoutePaths.LOGIN)) {
          window.location.replace(RoutePaths.LOGIN);
        }

        return result;
      }

      const { access_token, refresh_token } = refreshResult.data;
      AuthAccessor.setTokens({ access_token, refresh_token });

      result = await baseQuery(args, api, extraOptions);
    } catch (error) {
      console.error(errorSerializer(error as APIErrorResponse));
      LocalStorageApi?.removeItem(USER_ID);
      LocalStorageApi?.removeItem(USER_EMAIL);
      AuthAccessor.clearTokens();

      if (!location.pathname.includes(RoutePaths.LOGIN)) {
        window.location.replace(RoutePaths.LOGIN);
      }
    }
  }

  return result;
};
